import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { eventBus } from '../eventBus';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/Orders_CreateInput.ts';
import '../contracts/Orders_AddItemInput.ts';
import '../contracts/Orders_UpdateItemInput.ts';
import '../contracts/Orders_UpdateDiscountInput.ts';
import '../contracts/Orders_ConfirmInput.ts';
import '../contracts/Orders_RemoveItemInput.ts';
import '../contracts/Orders_CancelInput.ts';
import '../contracts/Orders_UncancelInput.ts';
import '../contracts/Orders_AddItemResponse';
import '../contracts/Orders_CancelResponse';
import '../contracts/Orders_UncancelResponse';
import '../contracts/Orders_ConfirmResponse';
import '../contracts/Orders_CreateResponse';
import '../contracts/Orders_DashboardResponse';
import '../contracts/Orders_GetResponse';
import '../contracts/Orders_ItemsResponse';
import '../contracts/Orders_ListaResponse';
import '../contracts/Orders_LookupByQrcodeResponse';
import '../contracts/Orders_RemoveItemResponse';
import '../contracts/Orders_UpdateDiscountResponse';
import '../contracts/Orders_UpdateItemResponse';
import '../contracts/Orders_CheckQrcodeUniqueResponse';
import type { Orders_AddItemInput } from '../contracts/Orders_AddItemInput';
import type { Orders_CreateInput } from '../contracts/Orders_CreateInput';
import type { Orders_UpdateDiscountInput } from '../contracts/Orders_UpdateDiscountInput';
import type { Orders_UpdateItemInput } from '../contracts/Orders_UpdateItemInput';
import type { Orders_CheckQrcodeUniqueResponse, Orders_CheckQrcodeUniqueResponseDados } from '../contracts/Orders_CheckQrcodeUniqueResponse';
/**
 * Composable for PDV Checkout operations.
 * Orchestrates order creation, item management, financial status,
 * and QR code lookup for the Frente de Caixa.
 *
 * Reuses SO endpoints — the PDV is a UI layer over the SO domain.
 */
export function useSOCheckout() {
  const isLoading = ref(false);
  const error = ref(null);
  const basePath = '/index.php/so/orders';

  /**
   * Surfaces a failed order write as a toast HERE, so no caller can swallow it
   * (ticket 2026-0029: "nova OS" silently did not save). The envelope's
   * `error` string is what the user sees; a thrown HTTP error keeps its message.
   */
  const surfaceError = (err: unknown, fallback: string): void => {
    const message = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', message || fallback);
  };

  const fetchOrders = async () => {
    log('useSOCheckout', 'fetchOrders', 'Fetching all orders');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista`, { responseContract: 'Orders_ListaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar ordens');
    } catch (err) {
      logError('useSOCheckout', 'fetchOrders', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getOrder = async (id: any) => {
    log('useSOCheckout', 'getOrder', 'Fetching order', { id });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/get/${id}`, { responseContract: 'Orders_GetResponse' });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao buscar ordem');
    } catch (err) {
      logError('useSOCheckout', 'getOrder', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const createOrder = async (data: Orders_CreateInput) => {
    log('useSOCheckout', 'createOrder', 'Creating order', data);
    isLoading.value = true;
    error.value = null;
    try {
      validate('Orders_CreateInput', data);
      const response = await httpService.post(`${basePath}/create`, data, { responseContract: 'Orders_CreateResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao criar ordem');
    } catch (err) {
      logError('useSOCheckout', 'createOrder', 'Error', err);
      surfaceError(err, 'Erro ao criar ordem');
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const addItem = async (orderId: any, data: any) => {
    log('useSOCheckout', 'addItem', 'Adding item', { orderId, data });
    isLoading.value = true;
    error.value = null;
    try {
      const payload: Orders_AddItemInput = { order_id: orderId, ...data };
      validate('Orders_AddItemInput', payload);
      const response = await httpService.post(`${basePath}/add_item`, payload, { responseContract: 'Orders_AddItemResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao adicionar item');
    } catch (err) {
      logError('useSOCheckout', 'addItem', 'Error', err);
      surfaceError(err, 'Erro ao adicionar item');
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const removeItem = async (itemId: number) => {
    log('useSOCheckout', 'removeItem', 'Removing item', { itemId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/remove_item/${itemId}`, null, { inputContract: 'Orders_RemoveItemInput', responseContract: 'Orders_RemoveItemResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao remover item');
    } catch (err) {
      logError('useSOCheckout', 'removeItem', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateItem = async (itemId: any, data: Orders_UpdateItemInput) => {
    log('useSOCheckout', 'updateItem', 'Updating item', { itemId, data });
    isLoading.value = true;
    error.value = null;
    try {
      validate('Orders_UpdateItemInput', data);
      const response = await httpService.post(`${basePath}/update_item/${itemId}`, data, { responseContract: 'Orders_UpdateItemResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao atualizar item');
    } catch (err) {
      logError('useSOCheckout', 'updateItem', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchItems = async (orderId: number) => {
    log('useSOCheckout', 'fetchItems', 'Fetching items', { orderId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/items/${orderId}`, { responseContract: 'Orders_ItemsResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar itens');
    } catch (err) {
      logError('useSOCheckout', 'fetchItems', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const confirmOrder = async (orderId: number) => {
    log('useSOCheckout', 'confirmOrder', 'Confirming order', { orderId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/confirm/${orderId}`, null, { inputContract: 'Orders_ConfirmInput', responseContract: 'Orders_ConfirmResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao confirmar ordem');
    } catch (err) {
      logError('useSOCheckout', 'confirmOrder', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const cancelOrder = async (orderId: number, reason: string) => {
    log('useSOCheckout', 'cancelOrder', 'Cancelling order', { orderId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/cancel/${orderId}`, { reason }, { inputContract: 'Orders_CancelInput', responseContract: 'Orders_CancelResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao cancelar ordem');
    } catch (err) {
      logError('useSOCheckout', 'cancelOrder', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const uncancelOrder = async (orderId: number) => {
    log('useSOCheckout', 'uncancelOrder', 'Uncancelling order', { orderId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/uncancel/${orderId}`, null, { inputContract: 'Orders_UncancelInput', responseContract: 'Orders_UncancelResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao descancelar ordem');
    } catch (err) {
      logError('useSOCheckout', 'uncancelOrder', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchDashboard = async () => {
    log('useSOCheckout', 'fetchDashboard', 'Fetching dashboard');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/dashboard`, { responseContract: 'Orders_DashboardResponse' });
      if (response.success) {
        return response.dados || {};
      }
      throw new Error(response.error || 'Erro ao buscar dashboard');
    } catch (err) {
      logError('useSOCheckout', 'fetchDashboard', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const lookupByQrcode = async (qrcode: any) => {
    log('useSOCheckout', 'lookupByQrcode', 'Looking up QR code', { qrcode });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lookup_by_qrcode/${encodeURIComponent(qrcode)}`, { responseContract: 'Orders_LookupByQrcodeResponse' });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'QR Code nao encontrado');
    } catch (err) {
      logError('useSOCheckout', 'lookupByQrcode', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateDiscount = async (orderId: any, discountType: any, discountValue: any) => {
    log('useSOCheckout', 'updateDiscount', 'Updating discount', { orderId, discountType, discountValue });
    isLoading.value = true;
    error.value = null;
    try {
      const payload: Orders_UpdateDiscountInput = {
        discount_type: discountType,
        discount_value: discountValue
      };
      validate('Orders_UpdateDiscountInput', payload);
      const response = await httpService.post(`${basePath}/update_discount/${orderId}`, payload, { responseContract: 'Orders_UpdateDiscountResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao atualizar desconto');
    } catch (err) {
      logError('useSOCheckout', 'updateDiscount', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * `orderId` names the order the item is going into, so the backend scopes the
   * check to THAT franchise. Without it the ADD path falls back to the session
   * filter — which refuses when nothing is selected and over-rejects when
   * several franchises are.
   *
   * Returns the whole answer: `unique` plus, when the QR is held, the order,
   * client and order status of the holder (`in_use_*`).
   */
  const checkQrcodeUnique = async (
    qrcode: string,
    excludeItemId?: number | string | null,
    orderId?: number | string | null,
  ): Promise<Orders_CheckQrcodeUniqueResponseDados> => {
    log('useSOCheckout', 'checkQrcodeUnique', 'Checking QR uniqueness', { qrcode, excludeItemId, orderId });
    const params: Record<string, string> = { qrcode };
    if (excludeItemId !== null && excludeItemId !== undefined && excludeItemId !== '') {
      params.order_item_id = String(excludeItemId);
    }
    if (orderId !== null && orderId !== undefined && orderId !== '') {
      params.order_id = String(orderId);
    }
    const response = await httpService.get<Orders_CheckQrcodeUniqueResponse>(`${basePath}/check_qrcode_unique`, {
      params,
      responseContract: 'Orders_CheckQrcodeUniqueResponse',
    });
    if (!response.success) {
      throw new Error(response.error || 'Erro ao verificar QR Code');
    }
    return { ...response.dados, unique: !!response.dados?.unique };
  };

  return {
    isLoading,
    error,
    fetchOrders,
    getOrder,
    createOrder,
    addItem,
    removeItem,
    updateItem,
    fetchItems,
    confirmOrder,
    cancelOrder,
    uncancelOrder,
    fetchDashboard,
    lookupByQrcode,
    updateDiscount,
    checkQrcodeUnique,
  };
}
